import React, { useState } from "react";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import { Plus, X, ArrowUpRight } from "@phosphor-icons/react";
import { useFormikContext } from "formik";

import AddLinkDialog from "./AddLinkDialog";
import type { GoalFormValues } from "./GoalBuilderPanel.types";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface FieldGroupProps {
  label: string;
  description?: string;
  required?: boolean;
  children: React.ReactNode;
}

const FieldGroup: React.FC<FieldGroupProps> = ({ label, description, required, children }) => (
  <Stack gap={2}>
    <Box>
      <Typography variant="body2" component="span">
        {label}
      </Typography>
      {required && (
        <Typography component="span" variant="body2" color="error.main">
          {" "}
          *
        </Typography>
      )}
      {description && (
        <Typography variant="caption" color="text.secondary" display="block">
          {description}
        </Typography>
      )}
    </Box>
    {children}
  </Stack>
);

// ---------------------------------------------------------------------------
// GoalFormSection2
// ---------------------------------------------------------------------------

/**
 * Section 2 — Mastery & Generalization.
 * Reads and writes form state via Formik context — must be rendered inside a
 * Formik provider (GoalBuilderPanel handles this).
 *
 * Fields: mastery_definition, generalization_definition, resources.
 */
const GoalFormSection2: React.FC = () => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<GoalFormValues>();

  const [addLinkOpen, setAddLinkOpen] = useState(false);

  const handleAddLink = (link: { url: string; title: string }) => {
    setFieldValue("resources", [...values.resources, link]);
  };

  const handleRemoveLink = (index: number) => {
    setFieldValue(
      "resources",
      values.resources.filter((_, i) => i !== index)
    );
  };

  return (
    <Stack
      component={"section"}
      data-testid="goal-form-section-2"
      direction={"column"}
      gap={2}
      p={3}
      sx={{
        bgcolor: (theme) => (theme.palette as any).grayscale?.[50] ?? "#F6F7F8",
        borderRadius: 3,
      }}
    >
      {/* Section header */}
      <Box pt={2} sx={{ px: 1 }}>
        <Typography component="h3" variant="subtitle2">
          Section 2 — Mastery &amp; Generalization
        </Typography>
        <Typography component="p" variant="caption" color="text.secondary">
          Define what mastery and generalization look like for this goal
        </Typography>
      </Box>

      {/* Fields card */}
      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: (theme) => (theme.palette as any).grayscale?.[100] ?? "#EAECEF",
          borderRadius: 3,
          p: 6,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <FieldGroup
          label="Mastery Definition"
          description="Describe the conditions under which this goal will be considered mastered — for example, a specific terminal target or completion of all targets."
          required
        >
          <TextField
            multiline
            minRows={4}
            fullWidth
            name="mastery_definition"
            value={values.mastery_definition}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.mastery_definition && Boolean(errors.mastery_definition)}
            helperText={touched.mastery_definition && errors.mastery_definition}
            aria-label="Mastery definition"
          />
        </FieldGroup>

        <FieldGroup
          label="Generalization Definition"
          description="Describe when and how skills under this goal should generalize — for example, across people, settings, or materials."
          required
        >
          <TextField
            multiline
            minRows={4}
            fullWidth
            name="generalization_definition"
            placeholder="e.g., Always use visual supports; session length 30 min max"
            value={values.generalization_definition}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.generalization_definition && Boolean(errors.generalization_definition)}
            helperText={touched.generalization_definition && errors.generalization_definition}
            aria-label="Generalization definition"
          />
        </FieldGroup>

        <FieldGroup
          label="Resources"
          description="Links to materials, videos, or references (optional)"
        >
          <Stack gap={3} alignItems="flex-start">
            {values.resources.map((resource, index) => (
              <Chip
                component="p"
                key={index}
                icon={<ArrowUpRight size={16} weight="regular" />}
                label={resource.title}
                title={resource.title}
                onDelete={() => handleRemoveLink(index)}
                deleteIcon={<X size={16} weight="regular" />}
                variant="outlined"
                sx={{
                  borderRadius: "10px",
                  height: 32,
                  "&.MuiChip-outlinedDefault": {
                    backgroundColor: "#FFF",
                  },
                  "& .MuiChip-label": { px: 4 },
                  "& .MuiChip-icon": { ml: 2, color: "text.secondary" },
                  "& .MuiChip-deleteIcon": { color: "text.secondary", mr: 2 },
                }}
                aria-label={`Resource: ${resource.title}`}
              />
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={(event) => {
                event.currentTarget.blur();
                setAddLinkOpen(true);
              }}
              aria-label="Add resource link"
              color="inherit"
            >
              Add link
            </Button>
          </Stack>
        </FieldGroup>
      </Box>

      <AddLinkDialog
        open={addLinkOpen}
        onClose={() => setAddLinkOpen(false)}
        onAdd={handleAddLink}
      />
    </Stack>
  );
};

export default GoalFormSection2;
